import cloneDeep from 'clone-deep'
import { type IFeeSplitLine, FeeSplitEditor, useFeeSplitPayees } from 'components/FeeSplits'
import type { InputType } from 'config'
import { useEffect, useState } from 'react'
import { getLoanChargeCategories, getLoanChargeLenders, getServicingNotes } from 'services'
import { Modal } from 'stories/components'
import {
  dailyRateDenominator,
  formatDate,
  getPrice2decimal,
  InputConvert,
  InputValidate,
  Num,
  removeComma,
  roundToCents,
} from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { chargeAccruedInterest } from './chargeInterest'
import {
  type ILoanCharge,
  type ILoanChargeCategory,
  type ILoanChargeLender,
  canWaiveCharge,
  isChargeWaived,
  LoanChargeOwedBy,
} from './types'

/**
 * This modal's Accrued Interest preview, read off the inputs. The calculation itself lives in
 * `chargeInterest` so it can be pinned against the server's figure on both Daily Rate Bases.
 */
const computeAccruedInterest = (inputs: Record<string, InputType>, denominator: number): number =>
  chargeAccruedInterest(
    Num(removeComma(inputs.unpaidBalance.value)),
    Num(removeComma(inputs.interestRate.value)),
    String(inputs.interestFromDate.value ?? ''),
    denominator,
  )

export const defaultInputs = (owedBy: LoanChargeOwedBy = LoanChargeOwedBy.Borrower): Record<string, InputType> => {
  const loanNumber = setLoanNumber()
  const isLenderCharge = owedBy === LoanChargeOwedBy.Lender
  return {
    date: {
      inputType: 'text',
      type: 'date',
      title: 'Date',
      required: true,
    },
    interestFromDate: {
      inputType: 'text',
      type: 'date',
      title: 'Interest From Date',
      required: false,
    },
    category: {
      inputType: 'select',
      title: 'Category',
      // Populated from the configurable fee-category list; left blank until it loads so a slow
      // fetch never silently drops the value on an existing charge.
      options: [],
      hasDefaultOption: true,
      defaultOptionText: 'Select a category',
      value: '',
      required: false,
    },
    lenderName: {
      inputType: 'select',
      title: 'Owed By (Lender)',
      // Filled from the loan's lenders. Values are the lender's name, since the loan's own Lender
      // usually has no investor record to key on and the server matches on name in that case.
      options: [],
      hasDefaultOption: true,
      defaultOptionText: "Select the loan's lender",
      value: '',
      // Only meaningful on a lender charge. Revealed once the lender list has loaded, so a slow
      // fetch never shows an empty picker.
      visible: false,
      required: false,
    },
    borrowerRecoverable: {
      inputType: 'checkbox',
      title: 'Borrower Recoverable',
      value: false,
      // A cost the lender advanced on the borrower's behalf rather than one they simply owe.
      // Ticking it bills the borrower for the same amount on a linked charge; what they pay
      // settles this one, and anything they never pay still comes off the lender's distribution.
      // Only ever on a lender charge - a borrower charge is already the borrower's to pay.
      visible: isLenderCharge,
      required: false,
    },
    description: {
      inputType: 'textarea',
      type: 'text',
      title: 'Description',
      value: '',
      span: 'full',
      required: false,
    },
    interestRate: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Interest Rate',
      prefix: '%',
      required: false,
    },
    originalBalance: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Original Balance',
      prefix: '$',
      required: true,
    },
    unpaidBalance: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Unpaid Balance',
      prefix: '$',
      required: true,
    },
    accruedInterest: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Accrued Interest',
      prefix: '$',
      // Read-only/derived: interest accrues daily on the unpaid balance from the Interest From Date
      // at the charge's rate, over the loan's Daily Rate Basis. Mirrors the server, which recomputes
      // and stores it - which is also why previewing it a day-count off is cosmetic, not persisted.
      disabled: true,
      required: false,
    },
    totalDue: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Total Due',
      prefix: '$',
      disabled: true,
      required: true,
    },
    deferred: {
      inputType: 'checkbox',
      title: 'Deferred',
      value: false,
      // Deferring rolls a charge into what the borrower owes later. A lender charge is not billed
      // to anybody - it comes off the lender's next distribution - so the option does not apply.
      visible: !isLenderCharge,
      required: true,
    },
    deferredReason: {
      inputType: 'textarea',
      type: 'text',
      title: 'Why is it being deferred? (optional)',
      value: '',
      rows: 2,
      span: 'full',
      // Asked only once the charge is actually being deferred, so the form does not grow a box
      // that means nothing on the ordinary charge. Revealed by the Deferred checkbox and by the
      // merge that loads an existing charge.
      visible: false,
      required: false,
    },
    installmentAmount: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Monthly Repayment',
      prefix: '$',
      value: '',
      // What the borrower is billed a month where this charge is being paid off little by little -
      // $100 a month against a $1,000 charge - rather than asked for in full. Blank or 0 is no
      // plan, which is how every charge behaved before this existed. Not offered on a lender
      // charge: that one is recovered out of a distribution, not billed to anybody monthly.
      visible: !isLenderCharge,
      required: false,
    },
    installmentStartDate: {
      inputType: 'text',
      type: 'date',
      title: 'First Installment Date',
      value: '',
      // Asked only once a monthly amount is set, the same way the deferral asks for its reason,
      // and prefilled from the charge's own date, which is when such a plan normally starts.
      visible: false,
      required: false,
    },
    files: {
      inputType: 'FileTable',
      title: 'File',
      required: false,
      acceptFileTypes: '.pdf',
      filePath: `loan/${loanNumber}/loanCharges`,
      span: 'full',
      showCategory: false,
      showStatus: false,
    },
  }
}

export const AddLoanCharge = ({
  index,
  item,
  owedBy = LoanChargeOwedBy.Borrower,
  onClose,
  onUpdate,
  onWaive,
}: {
  index: number
  item: ILoanCharge | null
  /**
   * Which side of the book this charge belongs to. Comes from the list the charge is being added
   * to rather than a picker in here, so a charge can never be filed against the section it is not
   * shown in.
   */
  owedBy?: LoanChargeOwedBy
  onClose: Function
  onUpdate: Function
  /**
   * Hand this charge to the waive dialog. Optional: a caller that cannot waive (the add form, a
   * read-only view) simply leaves it out and the panel below is not offered.
   */
  onWaive?: (item: ILoanCharge) => void
}) => {
  const [loading, setLoading] = useState(false)
  const [inputs, setInputs] = useState(defaultInputs(owedBy))
  const [splits, setSplits] = useState<IFeeSplitLine[]>(item?.splits || [])
  const [categories, setCategories] = useState<ILoanChargeCategory[]>([])
  // Once the split has been edited by hand it is left alone: changing the category must not throw
  // away a split somebody set up on this charge.
  const [splitsEdited, setSplitsEdited] = useState(false)
  const isLenderCharge = owedBy === LoanChargeOwedBy.Lender
  const [lenders, setLenders] = useState<ILoanChargeLender[]>([])
  /**
   * The loan's Daily Rate Basis denominator, which a charge's interest is divided by. `undefined`
   * until it is known, which is what keeps the preview blank rather than briefly a cent-or-more
   * out: it is not in any store, so it has to be fetched when the modal opens.
   */
  const [dayBasis, setDayBasis] = useState<number | undefined>(undefined)
  // A lender charge is not collected from the borrower, so there is no collected fee to split.
  const payees = useFeeSplitPayees(isLenderCharge ? undefined : setLoanNumber())

  // The loan's Daily Rate Basis, for the Accrued Interest preview. Only the servicing record has
  // it, and nothing on this screen has already loaded that record, so this modal fetches it.
  //
  // A failed fetch settles on the same 365 the server uses for a loan whose basis was never set,
  // rather than leaving the preview blank for good: Total Due is a required field, so a basis that
  // never arrives would otherwise make the charge unsubmittable. Both figures are recomputed and
  // stored server-side either way, so the worst a wrong denominator here costs is the preview.
  useEffect(() => {
    getServicingNotes()
      .then((res: any) => setDayBasis(dailyRateDenominator(res?.data?.dailyRateBasis)))
      .catch(() => setDayBasis(dailyRateDenominator(null)))
  }, [])

  // Only active categories are offered for new/edited charges, but a charge already carrying a
  // retired category keeps it in the list so editing something else does not quietly reset it.
  useEffect(() => {
    getLoanChargeCategories(true)
      .then((categories: ILoanChargeCategory[]) => {
        setCategories(categories || [])
        const values = (categories || []).map((c) => c.value).filter(Boolean)
        const current = String(item?.category || '')
        if (current && !values.includes(current)) values.push(current)
        setInputs((prev) => {
          const newInputs = cloneDeep(prev)
          newInputs.category.options = values
          return newInputs
        })
      })
      .catch(() => {})
  }, [])

  // The loan's lenders, for a lender charge. A charge already naming somebody who has since come
  // off the loan keeps them in the list, so editing something else does not quietly re-point it.
  useEffect(() => {
    if (!isLenderCharge) return
    getLoanChargeLenders()
      .then((res: any) => {
        const rows: ILoanChargeLender[] = [...(res?.lenders || []), ...(res?.participants || [])]
        setLenders(rows)
        const names = rows.map((l) => l.name).filter(Boolean)
        const current = String(item?.lenderName || '')
        if (current && !names.includes(current)) names.push(current)
        setInputs((prev) => {
          const newInputs = cloneDeep(prev)
          newInputs.lenderName.options = names
          newInputs.lenderName.visible = true
          return newInputs
        })
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (item) {
      // Merge onto the latest inputs rather than the mount-time snapshot, so this cannot clobber
      // the category options if their fetch has already landed.
      setInputs((prev) => {
        const newInputs = cloneDeep(prev)
        Object.keys(newInputs).forEach((key) => {
          if (['date', 'interestFromDate', 'installmentStartDate'].includes(key) && (item as any)[key])
            (item as any)[key] = (item as any)[key].substring(0, 10)
          newInputs[key].value = (item as any)[key]
        })
        // A charge that arrives already deferred shows the reason it was deferred for, so an edit
        // can correct it rather than having to re-tick the box to reach the field. Coerced to a
        // string because every charge booked before this field existed carries no value at all -
        // including every charge on screen while the SPA is live and the API has not rolled yet -
        // and an undefined would hand React an uncontrolled textarea that turns controlled on the
        // first keystroke.
        newInputs.deferredReason.value = newInputs.deferredReason.value || ''
        newInputs.deferredReason.visible = !isLenderCharge && !!newInputs.deferred.value
        // Same coercion and the same reveal rule for the repayment plan: a charge booked before
        // plans existed carries neither field, and the start date is only shown while a monthly
        // amount is actually set.
        newInputs.installmentAmount.value = Num(newInputs.installmentAmount.value) || ''
        newInputs.installmentStartDate.value = newInputs.installmentStartDate.value || ''
        newInputs.installmentStartDate.visible =
          !isLenderCharge && Num(removeComma(newInputs.installmentAmount.value)) > 0
        // Today's derived accrued interest / total are left to the effect below, which fires once
        // the basis lands. Blanked rather than left showing the snapshot stored at last edit, which
        // is a figure from whenever the charge was last saved and not what is owed now.
        recalcDerived(newInputs, undefined)
        return newInputs
      })
    }
  }, [])

  // Fill the preview once the basis is known, and refill it if it changes. Separate from the effect
  // that loads the charge because the two land in either order: the fetch can beat the mount merge.
  useEffect(() => {
    if (dayBasis === undefined) return
    setInputs((prev) => {
      const newInputs = cloneDeep(prev)
      recalcDerived(newInputs, dayBasis)
      return newInputs
    })
  }, [dayBasis])

  // Keep accruedInterest and totalDue in sync with the inputs they derive from. accruedInterest is
  // read-only (calculated); totalDue = unpaid balance + accrued interest.
  //
  // Both are held as two-decimal strings, because `thousandSep` neither rounds the third decimal
  // nor pads a missing second one: a rounded $8.80 would otherwise read `$8.8`. The submit handler
  // puts them back through removeComma, so the server still receives numbers - and recomputes both
  // from the balance, rate and from-date regardless, which is what makes them safe to format here.
  const recalcDerived = (newInputs: Record<string, InputType>, denominator: number | undefined) => {
    // Nothing is previewed while the loan's Daily Rate Basis is in flight. A figure computed on a
    // guessed denominator would read ~1.4% high on a 365-basis loan and then correct itself, and a
    // number that moves on its own is read as the charge changing rather than as the page loading.
    if (denominator === undefined) {
      newInputs.accruedInterest.value = ''
      newInputs.totalDue.value = ''
      return
    }
    const accruedInterest = computeAccruedInterest(newInputs, denominator)
    newInputs.accruedInterest.value = accruedInterest.toFixed(2)
    // Rounded again after the addition: a balance carrying a fraction of a cent of its own would
    // otherwise put one back into Total Due, for the same truncated display.
    newInputs.totalDue.value = roundToCents(Num(removeComma(newInputs.unpaidBalance.value)) + accruedInterest).toFixed(
      2,
    )
  }

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    if (['unpaidBalance', 'interestRate', 'interestFromDate'].includes(key)) recalcDerived(newInputs, dayBasis)
    // The reason exists only while the deferral does: revealed when Deferred goes on and cleared
    // with it when it goes off, so a lifted deferral never leaves its justification behind. The
    // server applies the same rule, so a stale reason cannot be saved from anywhere else either.
    if (key === 'deferred') {
      newInputs.deferredReason.visible = !isLenderCharge && !!value
      if (!value) newInputs.deferredReason.value = ''
    }
    // A repayment plan needs a first installment date, so setting the monthly amount asks for one
    // and defaults it to the charge's own date - which is when a plan agreed with the charge
    // starts. Clearing the amount takes the date with it, so a plan cannot be left half-entered
    // and then read as one by the server (which needs both to bill anything).
    if (key === 'installmentAmount') {
      const hasPlan = Num(removeComma(value)) > 0
      newInputs.installmentStartDate.visible = !isLenderCharge && hasPlan
      if (!hasPlan) newInputs.installmentStartDate.value = ''
      else if (!newInputs.installmentStartDate.value)
        newInputs.installmentStartDate.value = String(newInputs.date.value || '').substring(0, 10)
    }
    // Picking a category prefills its default split, the way it will be applied when the charge is
    // collected. A split already edited here wins — see splitsEdited.
    if (key === 'category' && !splitsEdited) {
      const category = categories.find((c) => c.value === value)
      setSplits(category?.defaultSplits || [])
    }
    setInputs(newInputs)
  }

  // The repayment plan as the form currently reads, for the summary under the fields. Counted off
  // what is still outstanding rather than the original balance, because that is what the plan has
  // left to collect - and the last installment is the stub where the amount does not divide.
  const planMonthly = Num(removeComma(inputs.installmentAmount.value))
  const planBalance = Num(removeComma(inputs.unpaidBalance.value))
  const planCount = planMonthly > 0 ? Math.max(1, Math.ceil(roundToCents(planBalance / planMonthly))) : 0
  const planLast = planCount > 0 ? roundToCents(planBalance - planMonthly * (planCount - 1)) : 0

  const onSubmit = async () => {
    let hasError = false

    let newInputs = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in inputs) {
      newInputs[key].error = InputValidate(newInputs[key])
      data[key] = newInputs[key].value
      if (newInputs[key].error) hasError = true
    }
    setInputs(newInputs)
    if (hasError) return
    ;['interestRate', 'originalBalance', 'unpaidBalance', 'accruedInterest', 'totalDue', 'installmentAmount'].map(
      (key) => {
        data[key] = removeComma(data[key])
      },
    )
    // An empty Monthly Repayment is no plan, and the server's `@Min(0)` wants a number for it.
    data.installmentAmount = Num(data.installmentAmount)
    data.installmentStartDate = data.installmentAmount > 0 ? String(data.installmentStartDate || '') : ''
    if (isLenderCharge) {
      // The name is what the server matches a charge to a distribution row on; the id is sent with
      // it when the lender has an investor record, so a rename cannot orphan the charge.
      data.owedBy = LoanChargeOwedBy.Lender
      // Nothing to bill monthly: a lender charge comes off the lender's next distribution.
      data.installmentAmount = 0
      data.installmentStartDate = ''
      data.lenderName = String(data.lenderName || '')
      data.lenderParticipantUserId = lenders.find((l) => l.name === data.lenderName)?.participantUserId || 0
      data.borrowerRecoverable = !!data.borrowerRecoverable
      data.splits = []
    } else {
      data.owedBy = LoanChargeOwedBy.Borrower
      // Nothing to recover: a borrower charge is already billed to the borrower.
      data.borrowerRecoverable = false
      // Half-filled rows (a payee with no share, or a share with nobody to pay) are dropped rather
      // than saved: the server would ignore them anyway, and keeping them looks like a live split.
      data.splits = splits
        .filter((row) => row.payeeType && Number(removeComma(row.percent ?? 0) || 0) > 0)
        .map((row) => ({ ...row, percent: Number(removeComma(row.percent)) }))
    }
    setLoading(true)
    onUpdate(data)
  }

  return (
    <Modal
      title={
        isLenderCharge
          ? item
            ? `Update Lender Charge - No.${index + 1}`
            : 'Add Lender Charge'
          : item
            ? `Update Loan Charge - No.${index + 1}`
            : 'Add Loan Charge'
      }
      titleOkay="Submit"
      loading={loading}
      onClose={() => onClose()}
      onOk={onSubmit}
      childLevel={2}
      isOpen
    >
      <div className="text-gray-900">
        <div className="w-144 grid gap-4 md:grid-cols-2 grid-cols-1 mb-3 mt-2">
          {Object.keys(inputs).map((key, index) => {
            let input = inputs[key]
            if (input.visible === false) return null
            return (
              <div className={`input md:col-span-${input.span || 1}`} key={index}>
                <RenderInput input={input} Key={key} onChange={onChange} />
              </div>
            )
          })}
        </div>
        {/*
          What the plan actually does, in the form where it is being set up: how many months it
          takes, what the last one comes to, and - the part a servicer has to be sure of - that a
          payoff still collects the whole balance rather than waiting out the schedule.
        */}
        {!isLenderCharge && planMonthly > 0 && (
          <div className="w-144 mb-3 rounded border border-gray-200 px-3 py-2 text-[13px] text-gray-500">
            {inputs.installmentStartDate.value ? (
              <>
                {planCount} monthly installment{planCount === 1 ? '' : 's'} of $
                {getPrice2decimal(planMonthly, false, true)}
                {planLast > 0 && planLast !== planMonthly ? (
                  <>, the last ${getPrice2decimal(planLast, false, true)},</>
                ) : null}{' '}
                to clear the ${getPrice2decimal(planBalance, false, true)} outstanding. The borrower&apos;s statement
                and amount due ask for one installment a month from{' '}
                {formatDate(String(inputs.installmentStartDate.value).substring(0, 10))}, and a missed one is carried
                into the next. A payoff collects whatever is left in full.
              </>
            ) : (
              <>A plan needs a first installment date. Without one the whole balance is billed, as before.</>
            )}
            {!!inputs.deferred.value && (
              <> This charge is deferred, so nothing bills until the deferral is lifted.</>
            )}
          </div>
        )}
        {/*
          Waiving is its own action with its own dialog, reached from an icon on the grid row. But
          opening the charge is where a servicer looks for what they can do with it, and finding
          only Deferred in here reads as "this platform cannot waive a charge" - which is what was
          reported. So the charge says, in the place they are already looking, whether it can be
          waived and how.
        */}
        {item && !isLenderCharge && onWaive && (
          <div className="w-144 mb-3 rounded border border-gray-200 px-3 py-2 text-[13px]">
            {canWaiveCharge(item) ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-gray-500">
                  Waiving forgives what is left on this charge instead of collecting it. The charge
                  stays on the loan as the record that the fee was charged and then waived, and you
                  can waive part of it.
                  {isChargeWaived(item) && (
                    <> ${getPrice2decimal(Num(item.waivedAmount), false, true)} has been waived so far.</>
                  )}
                </span>
                <button
                  type="button"
                  className="whitespace-nowrap rounded border border-amber-700 px-3 py-1 font-medium text-amber-700 hover:bg-amber-50"
                  onClick={() => onWaive(item)}
                >
                  Waive this charge
                </button>
              </div>
            ) : (
              <span className="text-gray-500">
                {isChargeWaived(item)
                  ? `This charge has already been waived in full - $${getPrice2decimal(
                      Num(item.waivedAmount),
                      false,
                      true,
                    )} was forgiven. Reverse the waiver from the charges list to bill it again.`
                  : 'Nothing is left on this charge to waive: it has already been settled.'}
              </span>
            )}
          </div>
        )}
        {!isLenderCharge && (
          <div className="w-144 mb-3">
            <FeeSplitEditor
              value={splits}
              payees={payees}
              onChange={(rows) => {
                setSplitsEdited(true)
                setSplits(rows)
              }}
            />
          </div>
        )}
      </div>
    </Modal>
  )
}
